// TeamsListLayer.jsx
import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getTeams,
  createTeams,
  updateTeams,
  deleteTeams,
  checkDuplicateTeams
} from '../api/teamsAPI';
import { getAll } from '../api';
import { fetchData } from '../utils/apiHelpers';
import Select from 'react-select';
import { getTechnicians, updateTechnicians } from '../api/techniciansAPI';

const TeamsListLayer = () => {
  const userProfile = useUserProfile();
  const [teamList, setTeamList] = useState([]);
  const [colourList, setColourList] = useState([]);
  const [newTeam, setNewTeam] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnicians, setSelectedTechnicians] = useState([]);
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const colourOptions = colourList.map(s => ({
    value: s.id,
    label: s.name,
    bootstrapName: s.bootstrapName,
  }));

  const fetchTeams = async () => {
    try {
      const data = await getTeams();
      setTeamList(data);
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };


  const fetchTechnicians = async () => {
    try {
      const data = await getTechnicians();
      setTechnicians(data);
    } catch (err) {
      console.error('Error fetching technicians:', err);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newTeam.teamName?.trim()) newErrors.teamName = 'Team Name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchTeams();
    fetchData(getAll, { apiName: 'colours' }, setColourList);
    fetchTechnicians();
  }, []);

  useEffect(() => {
    if (editingId && technicians.length) {
      const assigned = technicians
        .filter(t => (t.teams || []).includes(editingId))
        .map(t => ({ value: t.id, label: t.users?.name || t.users?.email }));
      setSelectedTechnicians(assigned);
    }
  }, [editingId, technicians]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (teamList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [teamList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTeam((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleEdit = (item) => {
    setNewTeam(item);
    setEditingId(item.id);
    const assigned = technicians
      .filter(t => (t.teams || []).includes(item.id))
      .map(t => ({ value: t.id, label: t.users?.name || t.users?.email }));
    setSelectedTechnicians(assigned);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await deleteTeams(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchTeams();
    } catch (error) {
      console.error('Error deleting team:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const duplicate = await checkDuplicateTeams(
        newTeam.teamName,
        editingId
      );
      if (duplicate.nameExists) {
        setErrors({ teamName: 'Team Name already exists' });
        return;
      }
      const cleanPayload = {};
      for (const key in newTeam) {
        if (newTeam[key] !== null && typeof newTeam[key] !== 'object') {
          cleanPayload[key] = newTeam[key];
        }
      }
      // append organisation id of the user to the payload
      const payload = {
        ...cleanPayload,
      };

      if (editingId) {
        await updateTeams(editingId, payload);
        const selectedIds = selectedTechnicians.map(t => t.value);
        const updates = technicians.map(async tech => {
          const teams = tech.teams || [];
          const hasTeam = teams.includes(editingId);
          const shouldHave = selectedIds.includes(tech.id);
          if (hasTeam !== shouldHave) {
            const newTeams = shouldHave
              ? [...teams, editingId]
              : teams.filter(id => id !== editingId);
            await updateTechnicians(tech.id, { teams: newTeams });
          }
        });
        await Promise.all(updates);
        await fetchTechnicians();
      } else {
        await createTeams(payload);
      }

      setShowModal(false);
      setEditingId(null);

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      setNewTeam({});
      await fetchTeams();
    } catch (error) {
      console.error('Error saving team:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('teams:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewTeam({});
              setEditingId(null);
              setSelectedTechnicians([]);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Team
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th></th>
                <th>Team Name</th>
                <th>Technicians</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teamList.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.colours?.bootstrapName ? (
                        <div
                        className={`rounded-circle bg-${item.colours.bootstrapName} d-flex align-items-center justify-content-center text-white fw-bold`}
                        style={{ width: '35px', height: '35px', fontSize: '12px' }}
                        >
                        {(item.teamName || '')
                            .split(' ')
                            .slice(0, 2)
                            .map(word => word[0])
                            .join('')
                            .toUpperCase()}
                        </div>
                    ) : (
                        '-'
                    )}
                  </td>
                  <td>{item.teamName}</td>
                  <td>
                    {technicians
                      .filter(t => (t.teams || []).includes(item.id))
                      .map((tech, idx) => (
                        <img
                          key={tech.id}
                          src={
                            tech.users?.userImage
                              ? `${process.env.REACT_APP_SPACES_BASE}/${tech.users.userImage}`
                              : `${process.env.REACT_APP_SPACES_BASE}/user.png`
                          }
                          alt="Tech"
                          style={{
                            width: '45px',
                            height: '45px',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '1px solid #ddd',
                            marginLeft: idx === 0 ? 0 : '-4px'
                          }}
                        />
                      ))}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('teams:update') && (
                        <button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => handleEdit(item)}>
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('teams:delete') && (
                        <button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => handleDelete(item.id)}>
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {teamList.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-2">
                    No Work Teams items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Team' : 'New Team'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Team Name *</label>
                    <input
                      className={`form-control ${errors.teamName ? 'is-invalid' : ''}`}
                      name="teamName"
                      value={newTeam.teamName || ''}
                      onChange={handleInputChange}
                    />
                    {errors.teamName && <div className="invalid-feedback">{errors.teamName}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Team Colour</label>
                    <Select
                      name="colourId"
                      value={colourOptions.find(opt => opt.value === newTeam.colourId) || null}
                      onChange={(opt) => setNewTeam(prev => ({ ...prev, colourId: opt?.value }))}
                      options={colourOptions}
                      placeholder="Select Team Colour"
                      formatOptionLabel={(opt) => (
                        <div className="d-flex align-items-center gap-2">
                          <span className={`d-inline-block rounded-1 bg-${opt.bootstrapName}`} style={{ width: '10px', height: '10px' }}></span>
                          {opt.label}
                        </div>
                      )}
                      isClearable
                    />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Description</label>
                    <textarea className="form-control" name="teamDescription" value={newTeam.teamDescription || ''} onChange={handleInputChange} />
                  </div>
                  {editingId && (
                    <div className="col-md-12">
                      <label className="form-label">Technicians</label>
                      <Select
                        isMulti
                        value={selectedTechnicians}
                        onChange={setSelectedTechnicians}
                        options={technicians.map(t => ({
                          value: t.id,
                          label: t.users?.name || t.users?.email
                        }))}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamsListLayer;
